import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  BookOpen, Flame, Target, Star, ChevronRight, 
  LogOut, Settings, Bookmark, Search, Award, Users, Trophy, Play,
  GraduationCap, CheckCircle2
} from "lucide-react";
import { API, useAuth, authFetch } from "@/App";
import OfflineDownloadButton from "@/components/OfflineDownloadButton";
import ThemeToggle from "@/components/ThemeToggle";
import TafsirOfTheDay from "@/components/TafsirOfTheDay";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [weeklyFocus, setWeeklyFocus] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [lastRead, setLastRead] = useState(null);
  const [pagePin, setPagePin] = useState(null);
  const [myLessons, setMyLessons] = useState([]);
  const [currentReciter, setCurrentReciter] = useState("ar.alafasy");

  useEffect(() => {
    fetchData();
  }, []);

  // First-time "Compare Reciters" nudge — shown ONCE to any user who hasn't
  // seen it. Reveals the 35-reciter catalog + A/B testing dialog behind the
  // default Alafasy selection. Sets a localStorage flag so it never repeats.
  // First-time "Compare Reciters" nudge. Rendered as a dismissible banner
  // below, NOT a sonner toast with action/onDismiss/onAutoClose — iOS Safari
  // throws `DataCloneError: the object can not be cloned` when sonner's
  // internal state contains function values (action.onClick, onDismiss,
  // onAutoClose). A plain JSX banner is always cloneable and safer.
  const [showNudge, setShowNudge] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("quran_compare_reciters_nudge_done") === "1") return;
    const t = setTimeout(() => setShowNudge(true), 1800);
    return () => clearTimeout(t);
  }, []);
  const dismissNudge = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_compare_reciters_nudge_done", "1");
    }
    setShowNudge(false);
  };
  const openCompareFromNudge = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran_open_compare_on_mount", "1");
    }
    dismissNudge();
    navigate("/reader/1?mode=flashcard");
  };

  // Mark a teacher-assigned lesson as complete. Removes it from the local
  // list immediately (optimistic) and the teacher's list reflects completed
  // on their next refresh.
  const completeLesson = async (lessonId) => {
    try {
      const r = await authFetch(
        `${API}/student/lessons/${lessonId}/complete`,
        { method: "PUT" },
      );
      if (r.ok) {
        setMyLessons((prev) => prev.filter((l) => l.lesson_id !== lessonId));
        toast.success("Lesson marked complete");
      } else {
        toast.error("Could not mark complete");
      }
    } catch {
      toast.error("Network error");
    }
  };


  const fetchData = async () => {
    try {
      const [statsRes, surahsRes, lastReadRes, settingsRes, focusRes, pinRes, lessonsRes] = await Promise.all([
        authFetch(`${API}/progress/stats`),
        fetch(`${API}/quran/surahs`),
        authFetch(`${API}/last-read`),
        authFetch(`${API}/settings`),
        authFetch(`${API}/focus-session/weekly`),
        authFetch(`${API}/page-pin`),
        authFetch(`${API}/student/lessons`),
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      if (focusRes.ok) {
        try {
          setWeeklyFocus(await focusRes.json());
        } catch (e) { console.error("weekly focus parse:", e); }
      }
      if (lastReadRes.ok) {
        const data = await lastReadRes.json();
        setLastRead(data?.last_read || null);
      }
      if (pinRes.ok) {
        try {
          const data = await pinRes.json();
          if (data?.page_pin?.page) setPagePin(data.page_pin);
        } catch (e) { console.error("page pin parse:", e); }
      }
      if (lessonsRes.ok) {
        try {
          const data = await lessonsRes.json();
          setMyLessons((data.lessons || []).filter((l) => !l.completed));
        } catch (e) { console.error("lessons parse:", e); }
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        const r = data?.memorization_settings?.reciter;
        if (r) setCurrentReciter(r);
      }

      if (surahsRes.ok) {
        const surahsData = await surahsRes.json();
        setSurahs(surahsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const filteredSurahs = surahs.filter(surah => 
    surah.englishName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    surah.name?.includes(searchQuery) ||
    surah.number?.toString().includes(searchQuery)
  );

  const progressPercentage = stats 
    ? Math.round((stats.total_ayahs_memorized / stats.total_quran_ayahs) * 100 * 100) / 100
    : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="min-h-screen bg-islamic-blue-950">
      {/* Header */}
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/hifz-logo.png"
              alt="Hifz"
              className="w-10 h-10 rounded-lg"
              data-testid="app-logo"
            />
            <span className="font-cinzel text-lg font-semibold text-slate-100">Hifz</span>
          </div>

          <nav className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/leaderboard">
              <Button 
                data-testid="leaderboard-nav-btn"
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
                title="Leaderboard"
              >
                <Trophy className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/groups">
              <Button 
                data-testid="groups-nav-btn"
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
                title="Family & Groups"
              >
                <Users className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/badges">
              <Button 
                data-testid="badges-nav-btn"
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
                title="Achievements"
              >
                <Award className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/bookmarks">
              <Button 
                data-testid="bookmarks-nav-btn"
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
              >
                <Bookmark className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/settings">
              <Button 
                data-testid="settings-nav-btn"
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
              >
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button 
              data-testid="logout-btn"
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-400 hover:bg-red-400/10"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <section className="mb-6">
          <h1 className="font-cinzel text-2xl sm:text-3xl font-bold text-slate-100 mb-2">
            Assalamu Alaikum, <span className="text-gold-400">{user?.name?.split(' ')[0] || 'User'}</span>
          </h1>
          <p className="text-slate-400 font-outfit">Continue your memorization journey</p>
        </section>

        {/* Guest upgrade banner */}
        {user?.is_guest && (
          <section
            data-testid="guest-upgrade-banner"
            className="mb-6 rounded-xl border border-gold-400/30 bg-gold-400/5 p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-full bg-gold-400/15 flex items-center justify-center text-gold-400 flex-shrink-0">
              <Star className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-cinzel text-slate-100 text-sm sm:text-base">
                You're signed in as a guest
              </p>
              <p className="text-slate-400 text-xs font-outfit mt-0.5">
                Add an email + password to sync progress across devices.
              </p>
            </div>
            <Link to="/settings?upgrade=1">
              <Button
                data-testid="upgrade-account-btn"
                size="sm"
                className="btn-gold rounded-full px-4"
              >
                Upgrade
              </Button>
            </Link>
          </section>
        )}

        {/* Continue where you left off */}
        {lastRead && (
          <section
            data-testid="continue-banner"
            onClick={() => {
              const url = lastRead.view_mode
                ? `/reader/${lastRead.surah_number}?mode=${lastRead.view_mode}`
                : `/reader/${lastRead.surah_number}`;
              navigate(url);
            }}
            className="mb-10 group cursor-pointer rounded-2xl border border-[#C9A227]/30 bg-gradient-to-r from-[#1B4D3E] via-[#0D2818] to-[#0D2818] hover:border-[#C9A227]/60 transition-all overflow-hidden relative"
          >
            <div
              className="absolute inset-0 opacity-[0.07] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 90% 30%, #C9A227 0, transparent 40%)",
              }}
            />
            <div className="relative flex items-center gap-4 p-5">
              <div className="w-12 h-12 rounded-full bg-[#C9A227] text-[#0D2818] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[#8B9D83] font-outfit mb-0.5">
                  Continue where you left off
                </p>
                <p className="text-[#E8D5A3] font-cinzel text-base sm:text-lg truncate">
                  {lastRead.surah_name || `Surah ${lastRead.surah_number}`}
                  <span className="text-[#C9A227]/80"> · Ayah {lastRead.ayah_number}</span>
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#C9A227] group-hover:translate-x-1 transition-transform" />
            </div>
          </section>
        )}

        {/* Stats Cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <div className="glass-card rounded-xl p-5 card-lift">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gold-400/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-gold-400" />
              </div>
              <span className="text-slate-400 text-sm font-outfit">Memorized</span>
            </div>
            <p className="font-cinzel text-2xl font-bold text-slate-100">
              {stats?.total_ayahs_memorized || 0}
            </p>
            <p className="text-slate-500 text-xs font-outfit">ayahs</p>
          </div>

          <div className="glass-card rounded-xl p-5 card-lift">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-500" />
              </div>
              <span className="text-slate-400 text-sm font-outfit">Today</span>
            </div>
            <p className="font-cinzel text-2xl font-bold text-slate-100">
              {stats?.today_reviewed || 0}
              <span className="text-lg text-slate-500">/{stats?.daily_goal || 5}</span>
            </p>
            <p className="text-slate-500 text-xs font-outfit">daily goal</p>
          </div>

          <div className="glass-card rounded-xl p-5 card-lift">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <span className="text-slate-400 text-sm font-outfit">Streak</span>
            </div>
            <p className="font-cinzel text-2xl font-bold text-slate-100">
              {stats?.streak || 0}
            </p>
            <p className="text-slate-500 text-xs font-outfit">days</p>
          </div>

          <div className="glass-card rounded-xl p-5 card-lift">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Star className="w-5 h-5 text-purple-500" />
              </div>
              <span className="text-slate-400 text-sm font-outfit">Surahs</span>
            </div>
            <p className="font-cinzel text-2xl font-bold text-slate-100">
              {stats?.surahs_started || 0}
            </p>
            <p className="text-slate-500 text-xs font-outfit">started</p>
          </div>
        </section>

        {/* First-time "Compare Reciters" nudge banner — replaces an
            unsafe sonner toast with function callbacks (iOS DataCloneError). */}
        {showNudge && (
          <div
            data-testid="compare-reciters-nudge"
            className="glass-card rounded-xl p-4 mb-4 flex items-center justify-between gap-3"
          >
            <div>
              <p className="font-cinzel text-slate-100 text-sm mb-0.5">
                Hear the Quran in 35 voices
              </p>
              <p className="text-slate-400 font-outfit text-xs">
                Try comparing 3 reciters side-by-side before you settle on a favourite.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                data-testid="compare-reciters-nudge-open"
                size="sm"
                onClick={openCompareFromNudge}
                className="btn-gold text-xs"
              >
                Open Compare
              </Button>
              <button
                data-testid="compare-reciters-nudge-dismiss"
                onClick={dismissNudge}
                aria-label="Dismiss"
                className="text-slate-500 hover:text-slate-300 p-1"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tafsir of the Day */}
        <TafsirOfTheDay />

        {/* My Lessons (student-side) */}
        {myLessons.length > 0 && (
          <section
            data-testid="my-lessons-section"
            className="glass-card rounded-xl p-5 mb-4"
          >
            <p className="font-cinzel text-slate-100 mb-3 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-gold-400" />
              My Lessons
              <span className="text-slate-500 text-xs font-outfit">
                ({myLessons.length})
              </span>
            </p>
            <ul className="space-y-2">
              {myLessons.slice(0, 5).map((l) => (
                <li
                  key={l.lesson_id}
                  data-testid={`lesson-item-${l.lesson_id}`}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-islamic-blue-950/40"
                >
                  <button
                    onClick={() =>
                      navigate(
                        `/reader/${l.surah_number}?mode=flashcard&ayah=${l.from_ayah}`,
                      )
                    }
                    className="flex-1 text-left min-w-0"
                  >
                    <p className="text-slate-200 text-sm font-outfit">
                      Surah {l.surah_number} · Ayah {l.from_ayah}
                      {l.to_ayah ? `–${l.to_ayah}` : "–end"}
                    </p>
                    {l.note && (
                      <p className="text-slate-400 text-xs mt-1 font-outfit italic">
                        “{l.note}”
                      </p>
                    )}
                    {l.feedback && (
                      <p
                        data-testid={`lesson-feedback-${l.lesson_id}`}
                        className="text-gold-300 text-xs mt-1 font-outfit"
                      >
                        <span className="font-semibold">Teacher:</span> {l.feedback}
                      </p>
                    )}
                    <p className="text-slate-500 text-xs mt-0.5 font-outfit">
                      from {l.teacher_name || "your teacher"}
                    </p>
                  </button>
                  <button
                    data-testid={`complete-lesson-${l.lesson_id}`}
                    onClick={() => completeLesson(l.lesson_id)}
                    className="text-slate-500 hover:text-green-400 p-2"
                    title="Mark complete"
                    aria-label="Mark complete"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Resume pinned page */}
        {pagePin?.page && (
          <button
            data-testid="resume-page-pin-card"
            onClick={() =>
              navigate(`/reader/${pagePin.surah_number || 1}?mode=page&page=${pagePin.page}`)
            }
            className="w-full glass-card rounded-xl p-5 mb-4 flex items-center justify-between card-lift text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gold-400/15 flex items-center justify-center">
                <Bookmark className="w-5 h-5 text-gold-400" />
              </div>
              <div>
                <p className="font-cinzel text-slate-100">
                  Resume your pinned page
                </p>
                <p className="text-slate-500 text-xs font-outfit">
                  Page {pagePin.page} · tap to jump back
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gold-400" />
          </button>
        )}

        {/* Weekly focus session card */}
        {weeklyFocus && weeklyFocus.session_count > 0 && (
          <section
            data-testid="weekly-focus-card"
            className="glass-card rounded-xl p-5 mb-10 flex items-center justify-between card-lift"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gold-400/10 flex items-center justify-center">
                <Star className="w-5 h-5 text-gold-400" />
              </div>
              <div>
                <p className="font-cinzel text-slate-100">
                  This week's focus time
                </p>
                <p className="text-slate-500 text-xs font-outfit">
                  Distraction-free reading sessions
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-cinzel text-2xl font-bold text-slate-100">
                {(() => {
                  const m = Math.round((weeklyFocus.total_seconds || 0) / 60);
                  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
                  return `${m}m`;
                })()}
              </p>
              <p className="text-slate-500 text-xs font-outfit">
                {weeklyFocus.session_count} session{weeklyFocus.session_count === 1 ? '' : 's'}
              </p>
            </div>
          </section>
        )}

        {/* Progress Overview */}
        <section className="glass-card rounded-xl p-6 mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-cinzel text-lg font-semibold text-slate-100">Overall Progress</h2>
            <span className="text-gold-400 font-outfit font-medium">{progressPercentage}%</span>
          </div>
          <Progress value={progressPercentage} className="h-3 bg-islamic-blue-800" />
          <p className="text-slate-500 text-sm font-outfit mt-3">
            {stats?.total_ayahs_memorized || 0} of {stats?.total_quran_ayahs || 6236} ayahs memorized
          </p>
        </section>

        {/* Surahs List */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-xl font-semibold text-slate-100">Browse Surahs</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="surah-search"
                type="text"
                placeholder="Search surahs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-islamic-blue-900/50 border border-white/10 rounded-lg text-slate-200 placeholder:text-slate-600 text-sm font-outfit focus:outline-none focus:border-gold-400/50 w-48"
              />
            </div>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="grid gap-3">
              {filteredSurahs.map((surah, index) => (
                <div
                  key={surah.number}
                  data-testid={`surah-card-${surah.number}`}
                  onClick={() => navigate(`/reader/${surah.number}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/reader/${surah.number}`);
                    }
                  }}
                  className="glass-card rounded-xl p-4 flex items-center gap-4 card-lift text-left w-full group cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold-400/40"
                  style={{ animationDelay: `${index * 0.02}s` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-gold-400/10 flex items-center justify-center font-cinzel text-gold-400 font-semibold">
                    {surah.number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-outfit font-medium text-slate-100 truncate">
                        {surah.englishName}
                      </h3>
                      <span className="text-slate-500 text-xs font-outfit">
                        {surah.numberOfAyahs} ayahs
                      </span>
                    </div>
                    <p className="text-slate-500 text-sm font-outfit truncate">
                      {surah.englishNameTranslation}
                    </p>
                  </div>
                  <p className="font-amiri text-xl text-gold-400/80 hidden sm:block">
                    {surah.name}
                  </p>
                  <OfflineDownloadButton
                    surahNumber={surah.number}
                    reciter={currentReciter}
                    size="sm"
                  />
                  <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-gold-400 transition-colors" />
                </div>
              ))}
            </div>
          </ScrollArea>
        </section>
      </main>
    </div>
  );
}
